/**
 * The platform's half of the Profile — contract C4 (`GET`/`PUT /api/v1/me/profile`), ADR 9012.
 *
 * The platform keeps one record per principal: a COPY of the e-mail, its verification and the
 * display name, taken from the verified token at every `PUT` — never from a body (a body carrying
 * them is a 422). So this file writes nothing but that refresh, and everything it shows is either
 * the record's copy or the token's claims:
 *
 * - **No alert e-mail** (bata-ui#166 W3-4, bata-platform ADR 2103; DEC-13: alerts are in-system
 *   only). The "Email me critical alerts" preference is RETIRED, not postponed: the platform no
 *   longer returns it and stores it `false` whatever is sent. The panel says where alerts are
 *   instead — the queue and its badge — rather than offering a switch nothing honours.
 * - **What is missing** — the record's `missing_fields`, each beside the action that fixes it.
 * - **A copy that lags the token.** After "Edit name", or once a new address is confirmed, the
 *   token knows more than the record. The record is refreshed only when the planner presses
 *   "Update my profile record": a `PUT` on opening the page would be a write nobody chose.
 *
 * A platform without the route (404 with no `user_profile` code) or without a readable store
 * (503 `platform.user_profile.store_unavailable`) is "not available yet", not a fault.
 */

import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { getMyProfile, putMyProfile } from '../../../api/generated';
import type { ProfileField, UserProfile } from '../../../api/generated';
import {
  ApiMalformedProblemError, ApiProblemError, useApiMutation, useApiQuery, useProblemMessage,
} from '../../../api';
import { changeEmail, editProfile, type IdentityClaims } from '../../../auth/session';
import { Button, EmptyState, Skeleton, StatusIndicator } from '../../../primitives';
import { Panel, ProblemNotice } from '../../../planning';

const CODE_PREFIX = 'platform.user_profile.';
export const PROFILE_STORE_UNAVAILABLE = 'platform.user_profile.store_unavailable';

/** Whether the platform cannot answer for profiles at all — a designed state, not a fault. */
export function isProfileNotLive(error: unknown): boolean {
  if (error instanceof ApiMalformedProblemError) return error.status === 404 || error.status === 501;
  if (!(error instanceof ApiProblemError)) return false;
  if (error.code === PROFILE_STORE_UNAVAILABLE) return true;
  return (error.status === 404 || error.status === 501) && !error.code.startsWith(CODE_PREFIX);
}

/**
 * Whether the record's copy says something other than the token does now. Compared field by
 * field on what the `PUT` would copy, so "different" means exactly "an update would change it".
 */
export function copyLagsToken(profile: UserProfile, identity: IdentityClaims): boolean {
  return (profile.email ?? null) !== identity.email
    || (profile.email_verified ?? null) !== identity.emailVerified
    || (profile.display_name ?? null) !== identity.name;
}

/** Whether the token already carries what the record lacks — then the fix is to update the record. */
export function tokenSupplies(field: ProfileField, identity: IdentityClaims): boolean {
  if (field === 'email') return identity.email !== null;
  if (field === 'email_verified') return identity.emailVerified === true;
  return identity.name !== null;
}

/** Each missing field as a sentence and the action that fixes it. Keys spelt out for i18n:coverage. */
const MISSING: Readonly<Record<ProfileField, { readonly labelKey: string; readonly actionKey: string; readonly act: () => void }>> = {
  display_name: { labelKey: 'profile.missing.display_name', actionKey: 'profile.personal.editName', act: editProfile },
  email: { labelKey: 'profile.missing.email', actionKey: 'profile.personal.addEmail', act: changeEmail },
  email_verified: { labelKey: 'profile.missing.email_verified', actionKey: 'profile.personal.changeEmail', act: changeEmail },
};

export function ProfileRecord({ identity }: { readonly identity: IdentityClaims }): React.JSX.Element {
  const query = useApiQuery(getMyProfile, {});

  if (query.isPending) {
    return <Panel titleKey="profile.record.title"><Skeleton rows={3} /></Panel>;
  }
  if (query.isError) {
    return (
      <Panel titleKey="profile.record.title">
        {isProfileNotLive(query.error) ? (
          <EmptyState
            variant="not-built"
            titleKey="profile.notifications.notLive.title"
            bodyKey="profile.notifications.notLive.body"
          />
        ) : (
          <ProblemNotice error={query.error} titleKey="profile.record.loadFailed" />
        )}
      </Panel>
    );
  }
  return <LoadedRecord profile={query.data} identity={identity} />;
}

function LoadedRecord({ profile, identity }: {
  readonly profile: UserProfile;
  readonly identity: IdentityClaims;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const problem = useProblemMessage();
  // One write, fully composed (ADR 0016): the PUT that re-copies the token. It carries no
  // preference — the only one there was, alert e-mail, is retired.
  const update = useApiMutation(putMyProfile, { body: {} });
  const refresh = (): void => { void queryClient.invalidateQueries({ queryKey: [getMyProfile.operationId] }); };
  const write = (): void => update.mutate(undefined, { onSuccess: refresh });
  const writing = update.isPending;
  const failed = update.error;

  const lags = copyLagsToken(profile, identity);

  return (
    <>
      <Panel titleKey="profile.notifications.title">
        <p className="text-body text-ink-secondary max-w-prose">{t('profile.alerts.inSystemOnly')}</p>
        {failed !== null && (
          <p role="alert" className="text-body text-ink-primary">
            {t('profile.record.writeFailed', { reason: problem(failed) })}
          </p>
        )}
      </Panel>

      <Panel titleKey="profile.record.title" subtitleKey="profile.record.subtitle">
        {lags && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-body text-ink-primary max-w-prose">
              {t(profile.saved ? 'profile.record.lags' : 'profile.record.neverSaved')}
            </p>
            <Button variant="secondary" loading={writing} onClick={write}>
              {t('profile.record.update')}
            </Button>
          </div>
        )}
        {profile.profile_complete ? (
          <StatusIndicator level="good" labelKey="profile.record.complete" />
        ) : (
          <ul className="flex flex-col gap-2" aria-label={t('profile.record.missingLabel')}>
            {profile.missing_fields.map((field) => {
              const missing = MISSING[field];
              // `missing_fields` describes the STORED copy. Where the sign-in already carries the
              // value, the fix is to update the record, not to send the planner to Keycloak again.
              const fromToken = tokenSupplies(field, identity);
              return (
                <li key={field} className="border-hairline flex flex-wrap items-center gap-3 border-b pb-2">
                  <StatusIndicator level="warning" labelKey={missing.labelKey} />
                  {fromToken ? (
                    <Button size="sm" variant="secondary" loading={writing} onClick={write}>
                      {t('profile.record.update')}
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={missing.act}>{t(missing.actionKey)}</Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </>
  );
}
